import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { CopyOutlined, DownOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  Col,
  Dropdown,
  Menu,
  Modal,
  Row,
  Table,
  Space,
  notification,
  Typography,
  message,
} from 'antd';
import { DateTime } from 'luxon';
import moment from 'moment';
import { FormHelpers, TaskHelpers } from 'ontraccr-common';
import * as Sentry from '@sentry/react';

import { getContactAddressBooks } from 'ontraccr-common/lib/Contacts';
import ManualEntryFormDrawer from '../../../../clock/ManualEntry/ManualEntryFormDrawer';
import OnTraccrButton from '../../../../common/buttons/OnTraccrButton';
import TimeEntryTableTeamSelection from './TimeEntryTableTeamSelection';
import TableDeleteButton from '../TableDeleteButton';
import { convertLowerCamelCaseToText, getIdMap, uuid } from '../../../../helpers/helpers';
import {
  getAllTimesText,
  loadManualEntryData,
  foundTaskConflict,
  loadTasks,
  foundTaskConflictForTimeEntryTable,
} from '../../../../clock/ManualEntry/manualEntryHelpers';
import FilteredUserSelector from '../../../../common/inputs/FilteredUserSelector';
import Permissions from '../../../../auth/Permissions';
import DisplayText from '../../../../common/text/DisplayText';
import { getHasColumnPermissions, getLinkId } from '../../../formHelpers';
import { getTablets } from '../../../../tablets/state/tablets.actions';
import TimeEntryTableInput from './TimeEntryTableInput';
import BorderlessButton from '../../../../common/buttons/BorderlessButton';
import {
  defaultFormatter,
  getColumnWidth,
  getSimpleSelectOptions,
  getTableInputParameters,
  parseCustomDataFromTemplate,
  collectPayableSubContractIds,
  simpleEquality,
  formatSelectedEntry,
  areStartTimesEqual,
  areEndTimesEqual,
  getRadioOrCheckboxWidth,
  addColumnSorting,
  getTimeEntryFieldTypesOrdered,
  getRelevantCostcodeIdsFromTimeEntries,
  renderNewlineSeparatedValues,
} from './TimeEntryTable.helpers';
import {
  EDITABLE_DEFAULT_FIELD_TYPES,
  getEditableDefaultFieldTypes,
  EDITABLE_CUSTOM_FIELD_TYPES,
  SHOW_INPUT_TYPES,
  FIELDS_THAT_TRIGGER_AUTO_BREAK_RECOMPUTE,
} from './TimeEntryTable.constants';
import { TIME_TYPE_FLAG } from '../../../../constants/Flags';
import { getResolvedCostcodeId, prepareResponsePayload } from '../../../ResponderHelpers';
import { getEnteredViaTableText, sortByStartTime } from '../../../../helpers/tasks';
import { sortEntries } from '../../../../timecards/timecardListSummaryView.helpers';
import { getCalculationTableColumn, getCustomTableColumn, LINE_NUMBER_COLUMN, createHandleReloadPreload } from '../formFieldsHelpers';

import useAutoBreak from './hooks/useAutoBreak';
import { pendingTableColumn, getConflictTableColumn } from '../../../forms.constants';
import useTimeEntryTableConflicts from '../../../hooks/useTimeEntryTableConflicts';
import TimeCardSlider from '../../../../timecards/TimeCardSlider/TimeCardSlider';
import TimecardsService from '../../../../timecards/state/timecards.service';
import { getProjectBillingRates } from '../../../../projectBillingRates/states/projectBillingRates.actions';
import FormsService from '../../../state/forms.service';
import { ENTRY_COSTCODE_COLUMNS } from '../../../hooks/useEntryCostcodes';
import useUpdateCostcodeIdMap from '../../../../costcodes/useUpdateCostcodeIdMap';

const { Link } = Typography;

const noop = () => {};

const DEFAULT_CUSTOM_FIELD_WIDTH = 260;

const getProjectsTasks = async ({
  preloadDateField,
  preloadDateModifier,
  preloadDateCount,
  linkedStart,
  linkedEnd,
  linkedProjectId,
  costcodeName,
  costcodeCode,
}) => {
  try {
    const now = DateTime.local();
    const startTimeToFilter = preloadDateField
      ? linkedStart
      : now.startOf('day').minus({ [preloadDateModifier]: preloadDateCount }).plus({ days: 1 });
    const endTimeToFilter = preloadDateField ? linkedEnd : now.endOf('day');

    const params = {
      projectId: linkedProjectId,
      startDate: startTimeToFilter.toISODate(),
      endDate: endTimeToFilter.toISODate(),
    };

    if (costcodeName && costcodeCode) {
      params.costcodeName = costcodeName;
      params.costcodeCode = costcodeCode;
    }

    const projectTasks = await loadTasks(params);

    return projectTasks;
  } catch (err) {
    Sentry.captureException(err);
    return null;
  }
};

const defaultTimeEntry = {
  // user - dynamic default
  // date - dynamic default
  type: 'work',
  // division - dynamic default
  project: null, // potentially dynamic based on selected project
  phase: null,
  costcode: null,
  local: null,
  class: null,
  sageShift: null,
  // hourBased: true,
  note: '',
  status: 'new',
};

const getEnteredViaColumn = (_, record) => getEnteredViaTableText(record.enteredViaMetadata);

const getTimeEntryColumnMap = ({
  requiredColumns,
  timezone,
  selectOptionProps,
  customDropdownProps,
  onUpdate,
  enableWarningThreshold,
  warningThreshold,
  preventEdits,
  settings = {},
  customColumnNames,
  usersWithAutoBreak,
  customFieldMap = {},
  defaultCustomData,
  paidFlags,
  stickyFieldKeys = new Set(),
  editableFieldTypes = EDITABLE_DEFAULT_FIELD_TYPES,
}) => {
  const timeEntryColumnMap = {};
  const orderedFieldTypes = getTimeEntryFieldTypesOrdered(paidFlags?.includes(TIME_TYPE_FLAG));

  orderedFieldTypes.forEach((fieldType) => {
    const col = {
      title: <div className={requiredColumns && 'form-required-field'}>{`${customColumnNames[fieldType]}`}</div>,
      dataIndex: fieldType,
      width: getColumnWidth(fieldType),
      key: fieldType,
    };
    col.onCell = (record) => {
      if (!record?.isUserHeader) return {};
      if (fieldType === 'user') {
        return {
          style: {
            width: '100%',
            minWidth: '100%',
            backgroundColor: '#e6f7ff',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
            overflow: 'visible',
          },
        };
      }
      return {
        style: {
          width: '0',
          display: 'none',
        },
      };
    };

    if (editableFieldTypes.includes(fieldType)) {
      col.setRender = (record) => (value, tableRecord) => {
        const realEntry = record ?? tableRecord;
        const {
          displayFormatter = defaultFormatter,
          areEqual = simpleEquality,
          getSelectOptions = getSimpleSelectOptions(fieldType),
          value: parsedValue = value,
          inputType = 'text',
          property = fieldType,
          readOnly = false,
          allowClear = true,
          inputStyle,
        } = getTableInputParameters({
          value,
          entry: realEntry,
          type: fieldType,
          enableWarningThreshold,
          warningThreshold,
          settings,
          paidFlags,
        });

        if (
          col.key === 'excludeFromAutoBreak'
          && !usersWithAutoBreak?.has(realEntry?.userId)
        ) {
          // Auto break not enabled for this user
          return null;
        }

        return (
          <TimeEntryTableInput
            title={`Update ${convertLowerCamelCaseToText(fieldType)}`}
            task={realEntry}
            value={parsedValue}
            fieldType={fieldType}
            inputType={inputType}
            property={property}
            displayFormatter={displayFormatter}
            getSelectOptions={getSelectOptions}
            selectOptionProps={selectOptionProps}
            customDropdownProps={customDropdownProps}
            timezone={timezone}
            areEqual={areEqual}
            width={getColumnWidth(fieldType)}
            onUpdate={onUpdate}
            readOnly={preventEdits || readOnly}
            allowClear={allowClear}
            showInput={SHOW_INPUT_TYPES.has(inputType) && !preventEdits}
            inputStyle={inputStyle}
            customFieldMap={customFieldMap}
            defaultCustomData={defaultCustomData}
            isSticky={stickyFieldKeys.has(fieldType)}
          />
        );
      };
    }

    timeEntryColumnMap[fieldType] = col;
  });
  return timeEntryColumnMap;
};

const groupEntriesByUserWithHeaders = (dataSource) => {
  const userGroups = {};

  dataSource.forEach((entry) => {
    const { userId, user: userName = 'Ontraccr' } = entry;
    if (!userGroups[userName]) {
      userGroups[userName] = {
        userId,
        userName,
        entries: [],
        totals: {
          total_hours: 0,
          total_break_hours: 0,
          total_overtime_hours: 0,
          total_double_ot_hours: 0,
          total_pto_hours: 0,
        },
      };
    }
    userGroups[userName].entries.push(entry);
    const runtimes = TaskHelpers.getRuntimes(entry);
    const {
      regularTime, breakTime, overtime, doubleOT, pto,
    } = runtimes;
    userGroups[userName].totals.total_hours += regularTime;
    userGroups[userName].totals.total_break_hours += breakTime;
    userGroups[userName].totals.total_overtime_hours += overtime;
    userGroups[userName].totals.total_double_ot_hours += doubleOT;
    userGroups[userName].totals.total_pto_hours += pto;
  });
  const hierarchicalData = [];
  Object.values(userGroups).forEach((userGroup) => {
    // Add user header row
    hierarchicalData.push({
      isUserHeader: true,
      userId: userGroup.userId,
      user: TaskHelpers.formatUserHeaderText(userGroup.userName, userGroup.totals),
      userGroupName: userGroup.userName,
      id: `user-header-${userGroup.userId}`,
      key: `user-header-${userGroup.userId}`,
    });
    // Add individual entries
    userGroup.entries
      .sort(sortByStartTime)
      .forEach((entry) => {
        const entryId = entry.id;
        hierarchicalData.push({
          ...entry,
          key: `${entryId}`,
        });
      });
  });
  return hierarchicalData;
};

const timeEntryData = ({
  onDelete,
  onEdit,
  onUpdate,
  onValueUpdated,
  onAddFromUserClicked,
  onCopy,
  groupByUser,
  columns,
  customFieldMap,
  defaultCustomData,
  isDisplay,
  requiredColumns,
  customDropdownProps,
  selectOptionProps,
  timezone,
  t,
  responding,
  dataSource,
  shouldTransposeData,
  shouldShowEmptyCustomFields,
  enableWarningThreshold,
  warningThreshold,
  preventEdits,
  columnPermissions = {},
  userPositionId,
  shouldShowAutoBreakColumn,
  usersWithAutoBreak,
  hasPendingTableData,
  paidFlags,
  lockEntryToAuthor,
  groupByUserSortOrder = null,
  conflictMap = {},
  onConflictClick,
  editableFieldTypes,
  divisionSet = new Set(),
  responses,
}) => {
  const {
    settings = {},
    projectIdMap = {},
  } = customDropdownProps;
  const cols = [];
  const orderedFieldTypes = getTimeEntryFieldTypesOrdered(paidFlags?.includes(TIME_TYPE_FLAG));
  const orderedFieldTypesSet = new Set(orderedFieldTypes);
  const customColumnNames = columns.reduce((acc, col) => {
    if (orderedFieldTypesSet.has(col.key)) {
      acc[col.key] = col.name;
    }
    return acc;
  }, {});

  const stickyFieldKeys = new Set(
    columns.filter((c) => c.isSticky).map((c) => c.key),
  );

  const timeEntryColumnMap = getTimeEntryColumnMap({
    requiredColumns,
    timezone,
    t,
    selectOptionProps,
    customDropdownProps,
    onUpdate,
    enableWarningThreshold,
    warningThreshold,
    preventEdits: preventEdits || isDisplay,
    settings,
    customColumnNames,
    usersWithAutoBreak,
    customFieldMap,
    defaultCustomData,
    paidFlags,
    stickyFieldKeys,
    editableFieldTypes,
  });

  if (hasPendingTableData) {
    cols.push(pendingTableColumn);
  }

  if (Object.keys(conflictMap).length) {
    cols.push(getConflictTableColumn({ conflictMap, onConflictClick }));
  }

  if (!isDisplay && !preventEdits && !lockEntryToAuthor) {
    cols.push({
      title: '',
      width: 50,
      dataIndex: '',
      key: 'copy-action',
      fixed: 'left',
      setRender: (record) => (_, tableRecord) => {
        const currentRecord = record ?? tableRecord;
        return (
          <BorderlessButton
            iconNode={<CopyOutlined />}
            onClick={() => onCopy(currentRecord)}
            style={shouldTransposeData ? { width: '100%' } : {}}
          />
        );
      },
    });
  }

  columns.forEach((col) => {
    if (
      !shouldShowAutoBreakColumn
      && col.key === 'excludeFromAutoBreak'
    ) return;
    if (!getHasColumnPermissions({
      columnPermissions,
      column: col,
      userPositionId,
      userId: Permissions.id,
    })) {
      return;
    }
    if (col.key in timeEntryColumnMap) {
      const tableColumn = { ...timeEntryColumnMap[col.key] };
      if (col.isSticky) {
        tableColumn.fixed = 'left';
      }
      cols.push(tableColumn);
    } else if (col.isCustomText) {
      const customTextCol = getCustomTableColumn(col, {
        width: 100,
        isDisplay,
        preventEdits: preventEdits || isDisplay,
        requiredColumns,
        responses,
        onChange: (record, value) => onValueUpdated(record.id, { [col.key]: value }),
      });
      const customTextRender = (val, record) => {
        if (record?.isUserHeader) return null;
        return customTextCol.render(val, record);
      };
      cols.push({
        ...customTextCol,
        setRender: () => customTextRender,
        onCell: (record) => {
          if (!record?.isUserHeader) return {};
          return {
            style: {
              width: '0',
              display: 'none',
            },
          };
        },
        ...(col.isSticky ? { fixed: 'left' } : {}),
      });
    } else if (col.isCalculation) {
      const calcCol = getCalculationTableColumn(col, {
        width: 100,
        isDisplay,
      });
      cols.push({
        ...calcCol,
        setRender: () => calcCol.render,
        ...(col.isSticky ? { fixed: 'left' } : {}),
      });
    } else if (col.key.startsWith('field-')) {
      // We only want to show custom fields if they have a value
      // We trim them because the date/time field will always have a value as its an empty space
      if (
        !shouldShowEmptyCustomFields
        && !dataSource?.some((entry) => !!entry[col.key]?.toString()?.trim?.())
      ) return;

      const {
        [col.key]: {
          type = '',
          id = '',
          configProps = {},
        } = {},
      } = customFieldMap;

      const { style, dataType, customOptions = [] } = configProps;

      const isCustomRadioOrCheckbox = dataType === 'Custom' && (style === 'radio' || style === 'checkbox');

      const width = isCustomRadioOrCheckbox
        ? getRadioOrCheckboxWidth(customOptions.slice(0, 10), style === 'radio')
        : DEFAULT_CUSTOM_FIELD_WIDTH;

      cols.push({
        title: <div className={requiredColumns && 'form-required-field'}>{col.name}</div>,
        width,
        dataIndex: col.key,
        ...(col.isSticky ? { fixed: 'left' } : {}),
        setRender: (record) => (value, tableRecord) => {
          const task = record ?? tableRecord;

          const {
            divisionId: taskDivId,
          } = task;

          const columnDivisionId = col.divisionId;
          if (columnDivisionId && taskDivId && columnDivisionId !== taskDivId) {
            return null;
          }

          if (EDITABLE_CUSTOM_FIELD_TYPES.has(type) && divisionSet.has(taskDivId)) {
            const {
              displayFormatter = defaultFormatter,
              areEqual = simpleEquality,
              getSelectOptions = getSimpleSelectOptions(type),
              value: parsedValue = value,
              inputType = 'text',
              readOnly = false,
              allowClear = true,
            } = getTableInputParameters({
              value, entry: task, type, id, settings, paidFlags, configProps,
            });

            const {
              title,
            } = configProps;

            return (
              <TimeEntryTableInput
                title={`Update ${title}`}
                task={task}
                fieldType={type}
                inputType={inputType}
                value={parsedValue}
                property={id}
                displayFormatter={displayFormatter}
                areEqual={areEqual}
                getSelectOptions={getSelectOptions}
                customDropdownProps={customDropdownProps}
                selectOptionProps={selectOptionProps}
                defaultCustomData={defaultCustomData}
                width={width}
                onUpdate={onUpdate}
                configProps={configProps}
                timezone={timezone}
                responding={responding}
                readOnly={preventEdits || readOnly || isDisplay}
                allowClear={allowClear}
                customFieldMap={customFieldMap}
                showInput={isCustomRadioOrCheckbox && !isDisplay}
                isSticky={col.isSticky}
              />
            );
          }

          if (type === 'table') {
            const lines = FormHelpers.getTableCellLines(task, col.key);
            if (lines) return renderNewlineSeparatedValues(lines);
          }
          return value;
        },
      });
    }
  });

  if (!isDisplay && !preventEdits) {
    cols.push({
      title: '',
      width: 10,
      dataIndex: '',
      setRender: (record) => (_, tableRecord) => {
        const currentRecord = record ?? tableRecord;
        if (currentRecord.isUserHeader) {
          return (
            <BorderlessButton
              iconNode={<PlusOutlined />}
              onClick={() => onAddFromUserClicked(currentRecord.userId)}
              style={shouldTransposeData ? { width: '100%' } : {}}
              title="Add Time Entry"
            />
          );
        }
        return (
          <BorderlessButton
            iconNode={<EditOutlined />}
            onClick={() => onEdit((record ?? tableRecord).id)}
            style={shouldTransposeData ? { width: '100%' } : {}}
          />
        );
      },
    });
    cols.push({
      title: '',
      width: 10,
      dataIndex: '',
      setRender: (record) => (_, tableRecord) => {
        const currentRecord = record ?? tableRecord;
        if (currentRecord.isUserHeader) return null;
        return (
          <TableDeleteButton
            onConfirm={() => onDelete((record ?? tableRecord).id)}
            style={shouldTransposeData ? { width: '100%' } : {}}
          />
        );
      },
    });
  }

  if (shouldTransposeData) {
    const rows = [{}, ...dataSource].map((entry, entryIndex) => ({
      title: '',
      dataIndex: '',
      width: '100%',
      render: (_value, _record, index) => {
        // 0th column is the column title row
        if (!entryIndex) {
          return (
            cols[index].title
          );
        }

        const relevantCol = cols[index];
        if (!relevantCol) return null;

        const { setRender, dataIndex } = relevantCol;
        if (!setRender) return entry[dataIndex];
        return setRender(entry)(entry[dataIndex], entry);
      },
      className: entry.pendingTableValue ? 'pending-table-value' : '',
    }));

    return {
      columns: rows,
      rows: cols,
    };
  }

  const ourColumns = cols.map((col) => {
    let render = col.setRender ? col.setRender() : (value) => value;
    if (col.key === 'enteredVia') render = getEnteredViaColumn;

    const column = {
      ...col,
      render,
    };

    if (!isDisplay || (groupByUser && col.key !== 'user')) return column;
    return addColumnSorting(column, { projectIdMap, settings, groupByUser, groupByUserSortOrder });
  });

  ourColumns.unshift(LINE_NUMBER_COLUMN);

  const userGroupedData = groupByUser
    ? groupEntriesByUserWithHeaders(dataSource)
    : dataSource;
  return {
    rows: userGroupedData,
    columns: ourColumns,
  };
};

export default function TimeEntryTablePreview({
  title = null,
  sections = [],
  columns = [],
  previewProps = {},
  id,
  setPreviewProps,
  isDisplay,
  setResponses,
  responses = {},
  responding = false,
  projectId: formProjectId,
  divisions = [],
  configProps = {},
  showCondensedView,
  t,
  createTimeTableIds,
  subContractMap = {},
  subContractCoMap = {},
  scheduleOfValues = {},

  customerToLabel = {},
  userToLabel = {},
  vendorToLabel = {},

  setCustomerIds,
  setProjectIds,
  setVendorIds,
  setContactIds,
  setCostcodeIds,
  setUserIds: setDropdownUserIds,
  setBucketIds,
  setSubContractIds,

  templateId: dropdownTemplateId,
  customerId,
  projectId,
  vendorId,

  selectedBucket = null,
  selectedBucketTypeToIdMap = {},
  selectedBucketTypes = [],
  costcodes = [],
  costcodeMap = {},
  fetchProjectCostcodes,
  inAddDrawer = false,
  enrichTimeEntryCustomData,
  poCloseMode = false,
}) {
  const dispatch = useDispatch();

  const users = useSelector((state) => state.users.users);
  const teams = useSelector((state) => state.teams.teams);
  const divisionMap = useSelector((state) => state.settings.divisions);
  const userDivisions = useSelector((state) => state.users.userDivisions);
  const projects = useSelector((state) => state.projects.projects);
  const phases = useSelector((state) => state.costcodes.phases);
  const { locals = [], classes = [] } = useSelector((state) => state.unions);
  const sageShifts = useSelector((state) => state.sage.shifts);
  const timeEntryUserMap = useSelector((state) => state.timeTracking.timeEntryUserMap);
  const formTemplates = useSelector((state) => state.forms.templates);
  const tablets = useSelector((state) => state.tablets);
  const customFields = useSelector((state) => state.timecards.customFields);
  const positionNames = useSelector((state) => state.settings.positionNames);
  const scheduleCategories = useSelector((state) => state.schedule.categories);
  const positions = useSelector((state) => state.settings.positions);
  const selectedForm = useSelector((state) => state.forms.selectedForm);

  const customers = useSelector((state) => state.customers.customers);
  const equipment = useSelector((state) => state.equipment.equipment);
  const projectEquipment = useSelector((state) => state.equipment.projectEquipment);
  const equipmentTypes = useSelector((state) => state.equipment.equipmentTypes);
  const vendors = useSelector((state) => state.vendors.vendors);
  const globalAddressBooks = useSelector((state) => state.contacts.globalAddressBooks);
  const labels = useSelector((state) => state.labels);
  const forms = useSelector((state) => state.forms.lightWeightForms);
  const subContractForms = useSelector((state) => state.forms.lightWeightSubContracts);
  const bucketTemplateMap = useSelector((state) => state.buckets.bucketTemplateMap);
  const buckets = useSelector((state) => state.buckets.buckets);
  const projectTypes = useSelector((state) => state.projects.projectTypes);
  const boardCardsForDropdown = useSelector((state) => state.boards.boardCardsForDropdown);
  const projectBillingRatesByProjectId = useSelector((state) => state.projectBillingRates.projectRates);
  const { settings = {}, paidFlags = [] } = useSelector((state) => state.settings.company);

  const classMap = useMemo(() => getIdMap(classes), [classes]);
  const localMap = useMemo(() => getIdMap(locals), [locals]);
  const phaseMap = useMemo(() => getIdMap(phases), [phases]);
  const projectMap = useMemo(() => getIdMap(projects), [projects]);
  const bucketIdMap = useMemo(() => getIdMap(buckets), [buckets]);
  const equipmentTypeMap = useMemo(() => getIdMap(equipmentTypes), [equipmentTypes]);
  const userMap = useMemo(() => getIdMap(users), [users]);
  const tabletMap = useMemo(() => getIdMap(tablets), [tablets]);
  const positionNameMap = useMemo(() => getIdMap(positionNames, 'name'), [positionNames]);

  const userPositionId = useMemo(() => {
    const userPosition = userMap[Permissions.id]?.position;
    return positionNameMap[userPosition]?.id;
  }, [positionNameMap, userMap]);

  const usersWithAutoBreak = useMemo(() => {
    const safePositions = positions || {};
    const positionsWithAutoBreak = new Set(
      Object.keys(safePositions).filter((position) => (
        safePositions[position]?.includes('AUTOMATIC_BREAKS')
      )),
    );
    return new Set(
      users
        .filter((user) => positionsWithAutoBreak.has(user.position))
        .map((user) => user.id),
    );
  }, [positions, users]);

  const activeProjects = useMemo(() => (projects.filter((project) => project.active)), [projects]);

  const contactAddressBooks = useMemo(() => (
    getContactAddressBooks(globalAddressBooks)
  ), [globalAddressBooks]);

  const [showDrawer, setShowDrawer] = useState(false);
  const [manualProjectId, setManualProjectId] = useState(null);
  const [showTeamSelect, setShowTeamSelect] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState();
  const [selectedUser, setSelectedUser] = useState();
  const [entriesToCopy, setEntriesToCopy] = useState(null);
  const [selectedUsersForCopy, setSelectedUsersForCopy] = useState([]);
  const [lastProjectId, setLastProjectId] = useState();
  const [lastDateValue, setLastDateValue] = useState({});
  const [lastCostcodeName, setLastCostcodeName] = useState();
  const [lastCostcodeCode, setLastCostcodeCode] = useState();
  const [hasPreloaded, setHasPreloaded] = useState(false);
  const [fileMap, setFileMap] = useState({});
  const [userIds, setUserIds] = useState([]);
  const [groupByUserSortOrder, setGroupByUserSortOrder] = useState(null);
  const [showConflictTimeCard, setShowConflictTimeCard] = useState(false);
  const [conflictTimeCard, setConflictTimeCard] = useState({
    entries: [],
    history: [],
    user: {},
    date: undefined,
  });
  const [prevRelevantCostcodeIds, setPrevRelevantCostcodeIds] = useState([]);

  const isMultiDivision = divisions.length > 1;
  const divisionSet = useMemo(() => new Set(divisions), [divisions]);
  const editableFieldTypes = useMemo(
    () => getEditableDefaultFieldTypes(isMultiDivision),
    [isMultiDivision],
  );

  const {
    hideAddNewButton,
    hideAddFromTeam,
    requiredColumns,
    lockEntryToAuthor,
    preloadExistingEntries,
    preloadDateCount = 1,
    preloadDateModifier = 'days',
    preloadProject,
    preloadDropdown,
    preloadStatuses = [],
    preloadDateField,
    preloadEntryDateType = 'currentDate',
    preloadEntryDateField,
    preloadEntryStartTimeField,
    preloadEntryEndTimeField,
    shouldTransposeData = false,
    shouldShowEmptyCustomFields = true,
    warnOnConflict = false,
    enableWarningThreshold = false,
    warningThreshold,
    preventEdits = false,
    columnPermissions = {},
    autoBreakEnabled,
    groupByUser = false,
    teamBasedTimeEntries = false,
    preloadCostcodeDropdown,
    preloadCostcodeName,
    preloadCostcodeCode,
  } = configProps;
  const values = previewProps.values || []; // For Responses
  const {
    selected: previewSelected = [],
    filterLabels = [],
    timezone: previewTimezone,
  } = previewProps;
  const {
    [id]: {
      values: responseSelected = [],
      timezone: responseTimezone,
    } = {},
  } = responses;

  const {
    calculateAutoBreak,
    resetAutoBreakState,
  } = useAutoBreak({
    autoBreakEnabled,
    settings,
    usersWithAutoBreak,
  });

  const shouldShowAutoBreakColumn = (
    autoBreakEnabled
    && Permissions.has('AUTO_BREAK_EXCLUDE')
  );

  const timezone = useMemo(() => {
    if (responseTimezone) return responseTimezone;
    if (previewTimezone) return previewTimezone;
    return DateTime.local().timezone;
  }, [responseTimezone, previewTimezone]);

  const ourCustomFields = useMemo(() => {
    const mergedFields = [];
    divisions.forEach((divId) => {
      const { [divId]: { fields = [] } = {} } = customFields;
      fields.forEach((field) => {
        if (!mergedFields.some(({ id }) => id === field.id)) {
          mergedFields.push({
            ...field,
            name: divisions.length > 1
              ? `${divisionMap[divId]?.name ?? divId}: ${field.name}`
              : field.name,
          });
        }
      });
    });
    return mergedFields;
  }, [customFields, divisions, divisionMap]);

  const { responses: defaultCustomData } = prepareResponsePayload({
    sections: ourCustomFields,
  });

  const getDefaultCustomDataForDivision = useCallback((divisionId) => {
    if (!divisionId) {
      return parseCustomDataFromTemplate({}, defaultCustomData);
    }
    const { [divisionId]: { fields: sections = [] } = {} } = customFields;
    const divisionSections = sections.filter(({ id }) => id !== 'timecard');
    if (!divisionSections.length) {
      return parseCustomDataFromTemplate({}, defaultCustomData);
    }
    const { responses } = prepareResponsePayload({ sections: divisionSections });
    return parseCustomDataFromTemplate({}, responses);
  }, [customFields, defaultCustomData]);

  const selected = useMemo(() => {
    let ourArray = [];
    if (isDisplay && !responding) {
      ourArray = values;
    } else if (responding) {
      ourArray = responseSelected;
    } else {
      ourArray = previewSelected;
    }
    return ourArray.map((entry) => {
      const costcodeId = getResolvedCostcodeId(entry.costcodeId);
      const withTimecardSystemFields = (customData = {}) => ({
        ...customData,
        // row.projectId/costcodeId are source of truth for conditionals
        project: {
          ...(customData.project ?? {}),
          value: entry.projectId ?? null,
        },
        costcode: {
          ...(customData.costcode ?? {}),
          value: costcodeId ?? null,
        },
      });

      if (!Array.isArray(entry.customData)) {
        if (!entry.customData) return entry;
        return {
          ...entry,
          customData: withTimecardSystemFields(entry.customData),
        };
      }
      // Revert formatting changes caused by constructFormPayloadForAPI
      const entryRef = { ...entry };

      const parsedCustomData = {};
      entryRef.customData.forEach(({ fields }) => {
        fields.forEach(({
          response, sectionId, fieldId, ...rest
        }) => {
          if (sectionId !== 'timecard') {
            parsedCustomData[fieldId] = {
              ...response,
              sectionId,
              fieldId,
              ...rest,
            };
          }
        });
      });
      delete entryRef.customData;
      const parsedFromTemplate = parseCustomDataFromTemplate(parsedCustomData, defaultCustomData);
      entryRef.customData = withTimecardSystemFields(parsedFromTemplate);
      return entryRef;
    });
  }, [responding, responseSelected, previewSelected, values, defaultCustomData]);

  const filteredActiveCostcodes = useMemo(() => (
    costcodes.filter((cc) => cc.canClockIn)
  ), [costcodes]);

  const hasPendingTableData = useMemo(
    () => selected.some((datum) => datum.pendingTableValue),
    [selected],
  );

  // Detect conflicts for the time entry table in pending state
  const conflictMap = useTimeEntryTableConflicts({
    fieldId: id,
    isDisplay,
    entries: selected,
    formId: selectedForm?.id,
    steps: selectedForm?.steps,
    templateSchema: selectedForm?.templateSchema,
    showOriginalTimes: settings?.showOriginalTimes,
    detectHourBasedConflict: settings?.detectHourBasedConflict,
    poCloseMode,
  });

  const openConflictTimeCard = useCallback(async (conflictingTask) => {
    notification.close('time-entry-conflict');
    const hide = message.loading('Loading time card...', 0);
    try {
          const { id: taskId, userId: taskUserId, startTime } = conflictingTask;
    const { data: history = [] } = await TimecardsService.getTimecardHistory(taskUserId, taskId);
    setConflictTimeCard({
      entries: [conflictingTask],
      history: history ?? [],
      user: userMap[taskUserId] ?? {},
      date: moment(startTime),
    });
    setShowConflictTimeCard(true);
    } finally {
      hide();
    }
  }, [userMap]);

  const onConflictClick = useCallback((record) => {
    const conflictingTask = conflictMap[record.id];
    if (!conflictingTask) return;
    const warnMessage = foundTaskConflict({
      conflictingTask,
      phases,
      projectIdMap: projectMap,
      userMap,
      costcodeIdMap: costcodeMap,
    });
    notification.error({
      key: 'time-entry-conflict',
      message: (
        <div>
          {warnMessage ?? 'This time entry conflicts with an existing time entry and will not be created.'}
          <div>
            <Link onClick={() => openConflictTimeCard(conflictingTask)}>
              View time card
            </Link>
          </div>
        </div>
      ),
      getContainer: null,
      placement: 'bottomRight',
      bottom: 50,
      duration: 10,
    });
  }, [conflictMap, phases, projectMap, userMap, costcodeMap, openConflictTimeCard]);

  const handleGroupByUserTableChange = useCallback((_pagination, _filters, sorter) => {
    const { columnKey, order } = sorter;
    if (columnKey === 'user') setGroupByUserSortOrder(order);
    else setGroupByUserSortOrder(null);
  }, []);

  const {
    id: userId,
    classId: defaultClassId,
  } = selectedUser || {};

  const customFieldMap = useMemo(() => {
    const relevantCustomFields = [];

    columns.forEach((col) => {
      if (!col.key?.startsWith('field-')) return;

      const sectionsSource = col.divisionId
        ? (customFields[col.divisionId]?.fields ?? []).filter(({ id }) => id !== 'timecard')
        : ourCustomFields.filter(({ id }) => id !== 'timecard');

      sectionsSource.forEach(({ fields: sectionFields = [] }) => {
        sectionFields.forEach((field) => {
          const { id: fieldId } = field;
          const isNestedTableKey = col.key.includes('-') && col.key.startsWith(`${fieldId}-`);
          if (fieldId === col.key || isNestedTableKey) {
            relevantCustomFields.push({
              id: col.key,
              type: field?.selectedType,
              configProps: field.configProps,
              divisionId: col.divisionId,
            });
          }
        });
      });
    });

    return getIdMap(relevantCustomFields);
  }, [columns, customFields, ourCustomFields]);

  const closeDrawer = useCallback(() => {
    setShowDrawer(false);
    setSelectedEntry();
    setManualProjectId(null);
    setUserIds([]);
  }, []);

  const handleManualProjectIdChange = useCallback((newProjectId) => {
    setManualProjectId(newProjectId);
    if (newProjectId) fetchProjectCostcodes?.(newProjectId);
  }, [fetchProjectCostcodes]);

  const closeTeamSelect = useCallback(() => setShowTeamSelect(false), []);
  const onAddFromTeamClicked = useCallback(() => setShowTeamSelect(true), []);

  const onCopyClicked = useCallback((record) => {
    const {
      isUserHeader,
      userId: copyUserId,
      id: copyId,
    } = record ?? {};
    const entries = isUserHeader
      ? selected.filter(({ userId: uId }) => uId === copyUserId)
      : [selected.find(({ id: eId }) => eId === copyId)];
    if (!entries?.length) return;
    setEntriesToCopy(entries);
    setSelectedUsersForCopy([]);
    setShowCopyModal(true);
  }, [selected]);

  const closeCopyModal = useCallback(() => {
    setShowCopyModal(false);
    setEntriesToCopy(null);
    setSelectedUsersForCopy([]);
  }, []);

  const createTimeEntry = useMemo(() => (
    createTimeTableIds?.includes(id)
  ), [createTimeTableIds, id]);

  const linkedProjectId = useMemo(() => (
    getLinkId({ responses, defaultValue: null, linkField: preloadDropdown })
  ), [responses, preloadDropdown]);

  const linkedCostcodeId = useMemo(() => {
    if (!preloadCostcodeDropdown) return null;
    const ourCostcodeId = getLinkId({
      responses,
      defaultValue: null,
      linkField: preloadCostcodeDropdown,
    }) ?? null;
    if (!ourCostcodeId) return null;
    return getResolvedCostcodeId(ourCostcodeId);
  }, [responses, preloadCostcodeDropdown]);

  const linkedCostcodeName = useMemo(() => {
    if (preloadCostcodeName) return preloadCostcodeName;
    if (!linkedCostcodeId || !costcodeMap[linkedCostcodeId]) return null;
    return costcodeMap[linkedCostcodeId]?.name ?? null;
  }, [preloadCostcodeName, linkedCostcodeId, costcodeMap]);

  const linkedCostcodeCode = useMemo(() => {
    if (preloadCostcodeCode) return preloadCostcodeCode;
    if (!linkedCostcodeId || !costcodeMap[linkedCostcodeId]) return null;
    return costcodeMap[linkedCostcodeId]?.code ?? null;
  }, [preloadCostcodeCode, linkedCostcodeId, costcodeMap]);

  const defaultProjectId = preloadProject ?? linkedProjectId ?? formProjectId;

  const projectBillingRateProjectId = manualProjectId ?? defaultProjectId;

  const projectBillingRateMap = useMemo(
    () => projectBillingRatesByProjectId?.[projectBillingRateProjectId],
    [projectBillingRatesByProjectId, projectBillingRateProjectId],
  );

  useEffect(() => {
    if (!projectBillingRateProjectId) return;
    dispatch(getProjectBillingRates(projectBillingRateProjectId));
  }, [projectBillingRateProjectId]);

  const linkedDateValue = useMemo(() => {
    if (!preloadDateField) return null;
    const {
      [preloadDateField]: dateResponse = {},
    } = responses;

    const { startTime, endTime, date } = dateResponse;

    if (date) { // date-time
      return {
        startTime: DateTime.fromMillis(date).startOf('day'),
        endTime: DateTime.fromMillis(date).endOf('day'),
      };
    }

    if (startTime && endTime) { // date-time range
      return {
        startTime: DateTime.fromMillis(startTime).startOf('day'),
        endTime: DateTime.fromMillis(endTime).endOf('day'),
      };
    }

    return null;
  }, [responses, preloadDateField]);

  const { preloadedDateMillis, preloadedDateSQL } = useMemo(() => (
    FormHelpers.getTimeEntryTablePreloadedDate({
      preloadEntryDateType,
      preloadEntryDateField,
      timezone,
      responses,
    })
  ), [preloadEntryDateField, preloadEntryDateType, responses, timezone]);

  const { preloadedStartTime, preloadedEndTime } = useMemo(() => (
    FormHelpers.getTimeEntryTablePreloadedStartEndTime({
      preloadedDateMillis,
      preloadEntryStartTimeField,
      preloadEntryEndTimeField,
      responses,
    })
  ), [
    preloadedDateMillis,
    preloadEntryStartTimeField,
    preloadEntryEndTimeField,
    responses,
  ]);

  const {
    preloadedSmartDateSQL = null,
    preloadedSmartStartTime = null,
  } = useMemo(() => (
    TaskHelpers.getPreloadedSmartTimeDate(selected, selectedUser, timezone)
  ), [selected, selectedUser, timezone]);

  const amendEntriesCallback = useCallback(({
    entries,
    startTimesChanged,
    endTimesChanged,
    isBeforeExisting,
    prevEntry,
  }) => {
    const useOriginal = settings?.showOriginalTimes;
    return TaskHelpers.amendSmartTimeEntryTableEntries({
      entries,
      startTimesChanged,
      endTimesChanged,
      isBeforeExisting,
      useOriginal,
      timezone,
      prevEntry,
    });
  }, [settings]);

  const userOpts = useMemo(() => {
    const allUsers = divisions?.map((divId) => {
      const {
        [divId]: {
          users: divisionUsers = new Set(),
        } = {},
      } = divisionMap;
      return Array.from(divisionUsers);
    }) ?? [];
    const userSet = new Set(allUsers.flat());
    return users.filter((user) => user.active && userSet.has(user.id));
  }, [users, divisionMap, divisions]);

  const onAddFromUserClicked = useCallback((newUserId) => {
    const newSelected = userMap[newUserId];
    if (!newSelected) return;
    setSelectedUser(newSelected);
    setSelectedEntry();
    setShowDrawer(true);
  }, [userMap]);

  const onAddNewClicked = useCallback(() => {
    const fallbackUserId = lockEntryToAuthor ? selectedForm?.userId : Permissions.id;
    setSelectedEntry();
    onAddFromUserClicked(userId ?? fallbackUserId ?? Permissions.id);
  }, [userId, lockEntryToAuthor, selectedForm]);

  const updateResponses = useCallback((newData = {}) => {
    const fullResponses = {
      ...responses,
      [id]: {
        ...(responses[id]),
        ...newData,
        timezone: timezone ?? DateTime.local().zoneName,
        createTimeEntry,
      },
    };
    setResponses(fullResponses);
  }, [
    responses,
    id,
    setResponses,
    createTimeEntry,
  ]);

  const handleCopyToUsers = useCallback(async () => {
    if (!entriesToCopy?.length || !selectedUsersForCopy.length) {
      closeCopyModal();
      return;
    }

    const newEntries = [];

    const {
      startTime,
      endTime,
    } = entriesToCopy.reduce((acc, entry) => {
      const date = TaskHelpers.getTaskDate(entry);
      acc.startTime = Math.min(acc.startTime, date.startOf('day').toMillis());
      acc.endTime = Math.max(acc.endTime, date.endOf('day').toMillis());
      return acc;
    }, {
      startTime: null,
      endTime: null,
    });

    const oldIdToNewUserIdMap = {};
    selectedUsersForCopy.forEach((targetUserId) => {
      const classId = userMap[targetUserId]?.classId;
      const localId = classMap[classId]?.localId;
      const localName = localMap[localId]?.name;
      const className = classMap[classId]?.name;
      const localWage = classMap[classId]?.wage;
      const userWage = userMap[targetUserId]?.wage;
      const finalUserWage = localWage ?? userWage;

      oldIdToNewUserIdMap[targetUserId] = [];

      entriesToCopy.forEach((entryToCopy) => {
        const newId = uuid();
        oldIdToNewUserIdMap[targetUserId].push({
          oldId: entryToCopy.id,
          newId,
        });
        const newEntry = {
          ...entryToCopy,
          id: newId,
          userId: targetUserId,
          user: userMap[targetUserId]?.name,
          localId,
          classId,
          local: localId ? localName : null,
          class: classId ? className : null,
          userWage: finalUserWage,
          hourlyCost: finalUserWage,
          status: 'new',
        };

        newEntries.push(newEntry);
      });
    });

    const fullEntries = selected.concat(newEntries);
    // Check for table entry specific conflicts
    // eslint-disable-next-line no-restricted-syntax
    for (const newEntry of newEntries) {
      const conflictMessage = foundTaskConflictForTimeEntryTable({
        tableEntries: fullEntries.filter((e) => e.userId === newEntry.userId),
        showOriginalTimes: settings?.showOriginalTimes,
      });

      if (conflictMessage) {
        notification.error({
          message: conflictMessage,
          getContainer: null,
          placement: 'bottomRight',
          bottom: 50,
          duration: 10,
        });
        return;
      }
    }

    // Check for conflicts if warnOnConflict is enabled
    if (warnOnConflict) {
      // Check conflicts for each new user
      // eslint-disable-next-line no-restricted-syntax
      for (const newEntry of newEntries) {
        const { id: entryId, userId: taskUserId } = newEntry;

        // Load existing entries for this user on this day
        // eslint-disable-next-line no-await-in-loop
        const existingUserEntries = await loadTasks({
          startTime,
          endTime,
          userId: taskUserId,
        });

        // Check if there's a conflict
        const warnMessage = foundTaskConflict({
          editIds: new Set([entryId]),
          currentTasks: existingUserEntries,
          newTasks: fullEntries.filter((e) => e.userId === taskUserId),
          phases,
          projectIdMap: projectMap,
          userMap,
          costcodeIdMap: costcodeMap,
          showOriginalTimes: settings?.showOriginalTimes,
          detectHourBasedConflict: settings?.detectHourBasedConflict,
          fromTimeTable: true,
        });

        if (warnMessage) {
          notification.error({
            message: warnMessage,
            getContainer: null,
            placement: 'bottomRight',
            bottom: 50,
            duration: 10,
          });
        }
      }
    }
    const userIsBeforeExisting = {};
    const userIdsToRecompute = new Set();
    const datesToRecompute = new Set();

    selectedUsersForCopy.forEach((targetUserId) => {
      const existingEntriesForUser = selected
        .filter((e) => e.userId === targetUserId && !e.hourBased && e.startTime);
      entriesToCopy.forEach((entryToCopy) => {
        if (existingEntriesForUser.length > 0) {
          const minExistingStartTime = Math.min(...existingEntriesForUser.map((e) => e.startTime));
          userIsBeforeExisting[targetUserId] = entryToCopy.startTime < minExistingStartTime;
        } else {
          userIsBeforeExisting[targetUserId] = false;
        }
        userIdsToRecompute.add(targetUserId);
        datesToRecompute.add(entryToCopy.date);
      });
    });
    const entriesByUser = TaskHelpers.getEntriesByUser(fullEntries);

    let amendedFullEntries = [];
    let hasZeroDurationError = false;
    Object.entries(entriesByUser).forEach(([userId, userEntries]) => {
      const { entries: amendedEntries, zeroDurationError } = amendEntriesCallback({
        entries: userEntries,
        startTimesChanged: true,
        endTimesChanged: true,
        isBeforeExisting: userIsBeforeExisting[userId] || false,
        prevEntry: null,
      });
      if (zeroDurationError) {
        notification.error({
          message: 'Invalid Time Entry',
          description: 'Copying these entries would cause an existing entry to have zero duration. Please adjust the times to avoid this conflict.',
          getContainer: null,
          placement: 'bottomRight',
          duration: 10,
        });
        hasZeroDurationError = true;
        return;
      }
      if (hasZeroDurationError) return;
      amendedFullEntries = amendedFullEntries.concat(amendedEntries);
    });

    const newSelected = await calculateAutoBreak(amendedFullEntries, {
      userIdsToRecompute,
      datesToRecompute,
      newUsers: selectedUsersForCopy,
      oldIdToNewUserIdMap,
      copiedFromUserId: entriesToCopy[0].userId,
    });

    // Reformat entries
    const formattedSelected = newSelected.map((task) => (
      TaskHelpers.formatTimeEntryTableEntry({
        entry: task,
        userId: task.userId,
        selectedEntry: task,
        classesMap: classMap,
        phaseMap,
        userMap,
        divisions: divisionMap,
        projectMap,
        costcodeMap,
        localMap,
        tabletMap,
        sageShifts,
        isPreload: true,
        generatedId: uuid(),
        getAllTimesText,
        companySettings: settings,
        projectBillingRateMap,
        positionNameMap,
      })
    ));

    if (responding) {
      updateResponses({
        values: formattedSelected,
        columns,
      });
    } else {
      setPreviewProps({
        ...previewProps,
        selected: formattedSelected,
      });
    }

    closeCopyModal();
  }, [
    entriesToCopy,
    selectedUsersForCopy,
    selected,
    userMap,
    classMap,
    localMap,
    warnOnConflict,
    phases,
    calculateAutoBreak,
    phaseMap,
    divisionMap,
    projectMap,
    costcodeMap,
    tabletMap,
    sageShifts,
    getAllTimesText,
    responding,
    updateResponses,
    columns,
    setPreviewProps,
    previewProps,
    closeCopyModal,
    settings,
    projectBillingRateMap,
    positionNameMap,
  ]);

  const onSubmitEntry = useCallback(async ({
    task: manualEntry,
    users: manualUsers,
    submitSelected = selectedEntry,
    changedProperty,
  } = {}) => {
    setUserIds([]);
    if (!manualEntry) return;
    const fallbackUserId = submitSelected?.userId ?? userId;
    const usersToCreateEntries = manualUsers?.length ? manualUsers : [fallbackUserId];
    const startTimesChanged = !areStartTimesEqual(manualEntry, submitSelected);
    const endTimesChanged = !areEndTimesEqual(manualEntry, submitSelected);
    const dateChanged = manualEntry?.date !== submitSelected?.date;
    const userIsBeforeExisting = {};
    const userPrevEntry = {};

    let newSelected = [...selected];

    const userIdsToRecompute = new Set();
    const datesToRecompute = new Set();
    const timesChangedEntries = [];
    const updatedTaskIds = [];

    if (submitSelected) {
      const prevEntryForEdit = selected.find((e) => e.id === submitSelected.id);
      if (prevEntryForEdit) {
        userPrevEntry[prevEntryForEdit.userId] = prevEntryForEdit;
      }
    }

    let hasTableEntryConflict = false;
    await Promise.all(usersToCreateEntries.map(async (user) => {
      const newEntry = TaskHelpers.formatTimeEntryTableEntry({
        entry: manualEntry,
        userId: user,
        selectedEntry: submitSelected,
        classesMap: classMap,
        phaseMap,
        userMap,
        divisions: divisionMap,
        projectMap,
        costcodeMap,
        localMap,
        sageShifts,
        generatedId: uuid(),
        getAllTimesText,
        companySettings: settings,
        projectBillingRateMap,
        positionNameMap,
      });
      if (!submitSelected && !newEntry.hourBased) {
        const existingEntriesForUser = newSelected
          .filter((e) => e.userId === user && e.id !== newEntry.id && !e.hourBased && e.startTime);
        if (existingEntriesForUser.length > 0) {
          const minExistingStartTime = Math.min(...existingEntriesForUser.map((e) => e.startTime));
          userIsBeforeExisting[user] = newEntry.startTime < minExistingStartTime;
        } else {
          userIsBeforeExisting[user] = false;
        }
      } else {
        userIsBeforeExisting[user] = false;
      }
      if (!newEntry.hourBased && !submitSelected) {
        const conflictMessage = foundTaskConflictForTimeEntryTable({
          tableEntries: [...newSelected.filter((e) => e.userId === newEntry.userId), newEntry],
          showOriginalTimes: settings?.showOriginalTimes,
          newEntry,
        });
        if (conflictMessage) {
          notification.error({
            message: conflictMessage,
            getContainer: null,
            placement: 'bottomRight',
            bottom: 50,
            duration: 10,
          });
          hasTableEntryConflict = true;
          return;
        }
      }
      if (submitSelected) {
        newSelected = newSelected.map((e) => (e.id === submitSelected.id ? newEntry : e));
      } else {
        newSelected = newSelected.concat([newEntry]);
      }

      if (warnOnConflict) {
        const {
          id: entryId,
          userId: taskUserId,
        } = newEntry;

        const taskDate = TaskHelpers.getTaskDate(newEntry);

        const startTime = taskDate.startOf('day').toMillis();
        const endTime = taskDate.endOf('day').toMillis();
        const existingUserEntries = await loadTasks({
          startTime,
          endTime,
          userId: user,
        });

        const warnMessage = foundTaskConflict({
          editIds: new Set([entryId]),
          currentTasks: existingUserEntries,
          newTasks: newSelected.filter((e) => e.userId === taskUserId),
          phases,
          projectIdMap: projectMap,
          userMap,
          costcodeIdMap: costcodeMap,
          showOriginalTimes: settings?.showOriginalTimes,
          detectHourBasedConflict: settings?.detectHourBasedConflict,
          fromTimeTable: true,
        });

        if (warnMessage) {
          notification.error({
            message: warnMessage,
            getContainer: null,
            placement: 'bottomRight',
            bottom: 50,
            duration: 10,
          });
          hasTableEntryConflict = true;
          return;
        }
      }

      let shouldRecompute = false;
      if (changedProperty) {
        /*
          From inline edit

          changedProperty is an array for times (e.g. startTime, otStartTime, etc)
        */
        shouldRecompute = Array.isArray(changedProperty)
          ? changedProperty.some((prop) => FIELDS_THAT_TRIGGER_AUTO_BREAK_RECOMPUTE.has(prop))
          : FIELDS_THAT_TRIGGER_AUTO_BREAK_RECOMPUTE.has(changedProperty);
      } else {
        // From manual entry
        shouldRecompute = Array.from(FIELDS_THAT_TRIGGER_AUTO_BREAK_RECOMPUTE).some((field) => (
          submitSelected?.[field] !== newEntry?.[field]
        ));
      }
      if (shouldRecompute) {
        userIdsToRecompute.add(newEntry.userId);
        datesToRecompute.add(newEntry.date);
        if (submitSelected?.date) datesToRecompute.add(submitSelected?.date);
        if (startTimesChanged || endTimesChanged) timesChangedEntries.push(newEntry);
        if (!(changedProperty === 'excludeFromAutoBreak' && newEntry.excludeFromAutoBreak) && !dateChanged) updatedTaskIds.push(newEntry.id);
      }
    }));
    if (hasTableEntryConflict) return;

    const entriesByUser = TaskHelpers.getEntriesByUser(newSelected);
    let tempNewSelected = [];
    let hasZeroDurationError = false;

    Object.entries(entriesByUser).forEach(([userId, userEntries]) => {
      const { entries: amendedEntries, zeroDurationError } = amendEntriesCallback({
        entries: userEntries,
        startTimesChanged,
        endTimesChanged,
        isBeforeExisting: userIsBeforeExisting[userId] || false,
        prevEntry: userPrevEntry[userId] || null,
      });
      if (zeroDurationError) {
        notification.error({
          message: 'Invalid Time Entry',
          description: 'This edit would cause an existing entry to have zero duration. Please adjust the times to avoid this conflict.',
          getContainer: null,
          placement: 'bottomRight',
          duration: 10,
        });
        hasZeroDurationError = true;
        return;
      }
      // Update newSelected with amended entries for this user
      tempNewSelected = tempNewSelected.concat(amendedEntries);
    });
    if (hasZeroDurationError) return;

    newSelected = tempNewSelected;
    let autoBreakRecomputeConfig = {};

    if (
      userIdsToRecompute.size
      || datesToRecompute.size
      || timesChangedEntries.length
      || updatedTaskIds.length
    ) {
      autoBreakRecomputeConfig = {
        userIdsToRecompute,
        datesToRecompute,
        timesChangedEntries,
        updatedTaskIds,
      };
    }

    newSelected = await calculateAutoBreak(
      newSelected,
      autoBreakRecomputeConfig,
    );

    // Need to reformat becaues times may have changed
    const formattedSelected = newSelected.map((task) => (
      TaskHelpers.formatTimeEntryTableEntry({
        entry: task,
        userId: task.userId,
        selectedEntry: task,
        classesMap: classMap,
        phaseMap,
        userMap,
        divisions: divisionMap,
        projectMap,
        costcodeMap,
        localMap,
        tabletMap,
        sageShifts,
        isPreload: true,
        generatedId: uuid(),
        getAllTimesText,
        companySettings: settings,
        projectBillingRateMap,
        positionNameMap,
      })
    ));

    if (responding) {
      updateResponses({
        values: formattedSelected,
        columns,
      });
    } else {
      setPreviewProps({
        ...previewProps,
        selected: formattedSelected,
      });
    }
    setSelectedEntry(null);
    setShowDrawer(false);
  }, [
    selected,
    userId,
    selectedEntry,
    responding,
    columns,
    userMap,
    divisionMap,
    projectMap,
    phaseMap,
    costcodeMap,
    localMap,
    classMap,
    sageShifts,
    updateResponses,
    phases,
    warnOnConflict,
    settings,
    calculateAutoBreak,
    projectBillingRateMap,
    positionNameMap,
  ]);

  const onEdit = useCallback((editId) => {
    const entry = selected.find(({ id: eId }) => eId === editId);
    if (!entry) return;
    setSelectedEntry(entry);

    const entryUser = userMap[entry.userId];

    setSelectedUser(entryUser);
    setShowDrawer(true);
  }, [selected, userMap]);

  const onDelete = useCallback(async (deletedId) => {
    let newSelected = selected.filter((item) => item.id !== deletedId);
    const deletedTask = selected.find((item) => item.id === deletedId);

    if (!deletedTask) return;

    const entriesByUser = TaskHelpers.getEntriesByUser(newSelected);

    let tempNewSelected = [];
    Object.values(entriesByUser).forEach((userEntries) => {
      const { entries: amendedEntries } = amendEntriesCallback({
        entries: userEntries,
        startTimesChanged: true,
        endTimesChanged: false,
        isBeforeExisting: false,
        prevEntry: null,
      });
      tempNewSelected = tempNewSelected.concat(amendedEntries);
    });

    newSelected = tempNewSelected;

    if (responding) {
      newSelected = await calculateAutoBreak(
        newSelected,
        {
          userIdsToRecompute: new Set([deletedTask.userId]),
          datesToRecompute: new Set([deletedTask.date]),
          deletedTask,
        },
      );

      // Need to reformat becaues times may have changed
      const formattedSelected = newSelected.map((task) => (
        TaskHelpers.formatTimeEntryTableEntry({
          entry: task,
          userId: task.userId,
          selectedEntry: task,
          classesMap: classMap,
          phaseMap,
          userMap,
          divisions: divisionMap,
          projectMap,
          costcodeMap,
          localMap,
          tabletMap,
          sageShifts,
          isPreload: true,
          generatedId: uuid(),
          getAllTimesText,
          companySettings: settings,
          projectBillingRateMap,
          positionNameMap,
        })
      ));
      updateResponses({
        values: formattedSelected,
        columns,
      });
    } else {
      setPreviewProps({
        ...previewProps,
        selected: selected.filter((item) => item.id !== deletedId),
      });
    }
  }, [
    responding,
    columns,
    previewProps,
    selected,
    updateResponses,
    calculateAutoBreak,
    classMap,
    phaseMap,
    userMap,
    divisionMap,
    projectMap,
    costcodeMap,
    localMap,
    tabletMap,
    sageShifts,
    getAllTimesText,
    settings,
    projectBillingRateMap,
    positionNameMap,
  ]);

  const onValueUpdated = useCallback((changedId, newData = {}) => {
    const newSelected = selected.map((item) => {
      if (item.id !== changedId) return item;
      return { ...item, ...newData };
    });
    if (responding) {
      updateResponses({
        values: newSelected,
        columns,
      });
    } else {
      setPreviewProps({
        ...previewProps,
        selected: newSelected,
      });
    }
  }, [
    selected,
    responding,
    updateResponses,
    columns,
    setPreviewProps,
    previewProps,
  ]);

  const onUpdate = useCallback((updateId, updatedEntryData, changedProperty) => {
    const entry = selected.find(({ id: eId }) => eId === updateId);
    if (!entry) return;
    setSelectedUser(userMap[entry.userId]);

    const clearedProjectFields = changedProperty === 'divisionId' ? {
      projectId: null,
      project: null,
      phaseId: null,
      phase: null,
      costcodeId: null,
      costcode: null,
    } : {};

    const mergedEntry = { ...entry, ...updatedEntryData, ...clearedProjectFields };

    let updatedCustomData;
    if (changedProperty === 'divisionId') {
      updatedCustomData = getDefaultCustomDataForDivision(updatedEntryData.divisionId);
    } else {
      updatedCustomData = parseCustomDataFromTemplate(
        {
          ...(entry.customData ?? {}),
          ...(updatedEntryData.customData ?? {}),
        },
        defaultCustomData,
      );
    }

    // Calculate hours per user per day (similar to ManualEntryFormDrawer logic)
    const entryDate = mergedEntry.date;

    // Find all entries for the same user on the same day
    const userDayEntries = selected.filter((e) => {
      if (e.userId !== entry.userId || !e.date) return false;
      return e.date === entryDate;
    });

    // Sum up time data for all entries (using merged entry for the one being updated)
    let totalTime = 0;
    let workTime = 0;
    let breakTime = 0;

    userDayEntries.forEach((task) => {
      const taskToUse = task.id === updateId ? mergedEntry : task;
      const { type: entryType } = taskToUse;
      const runtimes = TaskHelpers.getRuntimes(taskToUse, true);
      const workRuntime = runtimes.regularTime + runtimes.overtime + runtimes.doubleOT;
      const breakRuntime = runtimes.breakTime;
      const totalRuntime = workRuntime + breakRuntime;

      const isBreak = entryType === 'break';
      totalTime += totalRuntime;

      if (!isBreak) {
        workTime += workRuntime;
      } else {
        breakTime += breakRuntime;
      }
    });

    updatedCustomData.total_hours = {
      value: TaskHelpers.convertMillisecondsToHours(totalTime),
    };
    updatedCustomData.work_hours = {
      value: TaskHelpers.convertMillisecondsToHours(workTime),
    };
    updatedCustomData.break_hours = {
      value: TaskHelpers.convertMillisecondsToHours(breakTime),
    };

    // Handle specific field changes
    if (changedProperty === 'projectId') {
      updatedCustomData.project = { value: updatedEntryData.projectId };
      updatedCustomData.costcode = { value: null };
      updatedCustomData.costcode_code = { value: null };
      updatedCustomData.costcode_name = { value: null };
    } else if (changedProperty === 'phaseId') {
      updatedCustomData.costcode = { value: null };
      updatedCustomData.costcode_code = { value: null };
      updatedCustomData.costcode_name = { value: null };
    } else if (changedProperty === 'costcodeId') {
      const { costcodeId: fullCostcodeId } = updatedEntryData;
      const costcodeId = getResolvedCostcodeId(fullCostcodeId);
      updatedCustomData.costcode = { value: costcodeId };
      updatedCustomData.costcode_code = { value: costcodeMap[costcodeId]?.code };
      updatedCustomData.costcode_name = { value: costcodeMap[costcodeId]?.name };
    } else if (changedProperty === 'divisionId') {
      updatedCustomData.project = { value: null };
      updatedCustomData.costcode = { value: null };
      updatedCustomData.costcode_code = { value: null };
      updatedCustomData.costcode_name = { value: null };
    } else {
      // Keep project/costcode synced for conditionals when other fields change
      updatedCustomData.project = {
        ...(updatedCustomData.project ?? {}),
        value: mergedEntry.projectId ?? null,
      };
      updatedCustomData.costcode = {
        ...(updatedCustomData.costcode ?? {}),
        value: getResolvedCostcodeId(mergedEntry.costcodeId) ?? null,
      };
    }

    onSubmitEntry({
      task: {
        ...mergedEntry,
        customData: updatedCustomData,
        customDataArray: null, // Clear so customData is used and timecard section is rebuilt
      },
      submitSelected: entry,
      changedProperty,
    });
  }, [
    onSubmitEntry,
    selected,
    userMap,
    costcodeMap,
    defaultCustomData,
    getDefaultCustomDataForDivision,
  ]);

  /*
   * After selecting a team Id,
   * Generate a row with default values for each team member
   */
  const onAddFromTeam = useCallback(async (teamId) => {
    const selectedTeam = teams.find(({ id: tId }) => tId === teamId);
    const { members = [], divisionIds = [] } = selectedTeam;
    const { enableHourBasedTracking = false } = settings;

    // Use the team's first division that is also available on this form
    let divisionId = divisionIds.find((teamDivisionId) => divisionSet.has(teamDivisionId));

    if (!divisionId) {
      divisionId = divisions?.[0];
    }
    if (!members.length) {
      closeTeamSelect();
      return;
    }
    const newEntries = [];

    let needsRecompute = false;
    const userIdsToRecompute = new Set();
    const datesToRecompute = new Set();
    const filteredMembers = members.filter(({ id: mId }) => userMap[mId]?.active);

    if (teamBasedTimeEntries) {
      setUserIds(filteredMembers.map((member) => member.id));
      setSelectedUser();
      setSelectedEntry();
      setShowDrawer(true);
      closeTeamSelect();
      return;
    }

    filteredMembers.forEach((member) => {
      const classId = userMap[member.id]?.classId;
      const localId = classMap[classId]?.localId;
      const localName = localMap[localId]?.name;
      const className = classMap[classId]?.name;

      const localWage = classMap[classId]?.wage;
      const userWage = userMap[member.id]?.wage;
      const finalUserWage = localWage ?? userWage;
      const {
        preloadedSmartStartTime: smartStartTime,
        preloadedSmartDateMillis: smartDateMillis,
      } = TaskHelpers.getPreloadedSmartTimeDate(selected, member, timezone, preloadedDateMillis);

      const newEntry = {
        ...defaultTimeEntry,
        id: uuid(),
        dateTimestamp: smartDateMillis,
        date: TaskHelpers.formatDate(smartDateMillis),
        userId: member.id,
        user: userMap[member.id]?.name,
        divisionId,
        division: divisionMap[divisionId]?.name,
        hourBased: enableHourBasedTracking,
        projectId: defaultProjectId,
        project: defaultProjectId ? projectMap[defaultProjectId]?.name : undefined,
        localId,
        classId,
        local: localId ? localName : null,
        class: classId ? className : null,
        userWage: finalUserWage,
        hourlyCost: finalUserWage,
      };

      if (defaultCustomData) {
        newEntry.customData = {
          ...defaultCustomData,
          project: {
            ...(defaultCustomData.project ?? {}),
            value: defaultProjectId ?? null,
          },
        };
      }
      if (smartStartTime && !enableHourBasedTracking) {
        newEntry.startTime = smartStartTime;
        newEntry.originalStart = smartStartTime;
      } else if (preloadedStartTime && !preloadedEndTime) {
        newEntry.startTime = preloadedStartTime;
        newEntry.originalStart = preloadedStartTime;
        newEntry.hourBased = false;
      }
      if (preloadedStartTime && preloadedEndTime) {
        needsRecompute = true;
        userIdsToRecompute.add(newEntry.userId);
        datesToRecompute.add(newEntry.date);
        newEntry.startTime = preloadedStartTime;
        newEntry.originalStart = preloadedStartTime;
        newEntry.endTime = preloadedEndTime;
        newEntry.originalEnd = preloadedEndTime;
        newEntry.hourBased = false;
        const runtimes = TaskHelpers.getRuntimes(newEntry, false);
        /* eslint-disable max-len */
        newEntry.total_hours = TaskHelpers.convertMillisecondsToHours(runtimes.regularTime).toString();
        newEntry.work_hours = TaskHelpers.convertMillisecondsToHours(runtimes.regularTime).toString();
        newEntry.break_hours = TaskHelpers.convertMillisecondsToHours(runtimes.breakTime).toString();
        newEntry.pto_hours = TaskHelpers.convertMillisecondsToHours(runtimes.pto).toString();
        newEntry.formattedDuration = TaskHelpers.getFormattedDuration(runtimes);
        /* eslint-enable max-len */
        newEntry.formattedStartTime = getAllTimesText?.({
          ...newEntry,
          endTime: null,
          originalEnd: null,
          breakEndTime: null,
          otEndTime: null,
          doubleOTEndTime: null,
          ptoEndTime: null,
        }, {
          joinText: '\n',
          showRegularTypeText: false,
          addTypeAsSuffix: true,
          returnComponent: false,
          showOriginalTimes: settings?.showOriginalTimes,
        });
        newEntry.formattedEndTime = getAllTimesText?.({
          ...newEntry,
          startTime: null,
          originalStart: null,
          breakStartTime: null,
          otStartTime: null,
          doubleOTStartTime: null,
          ptoStartTime: null,
        }, {
          joinText: '\n',
          showRegularTypeText: false,
          addTypeAsSuffix: true,
          returnComponent: false,
          showOriginalTimes: settings?.showOriginalTimes,
        });
        newEntry.time = getAllTimesText(
          { ...newEntry, hourBased: true }, /* HARBOUR-6399: Always format time column as hours */
          {
            returnComponent: false,
            joinText: '\n',
            showRegularTypeText: false,
            addTypeAsSuffix: true,
            showOriginalTimes: settings?.showOriginalTimes,
          },
        );
      }

      newEntries.push(newEntry);
    });

    const fullEntries = selected.concat(newEntries);
    let newSelected = needsRecompute
      ? await calculateAutoBreak(fullEntries, {
        userIdsToRecompute,
        datesToRecompute,
      })
      : fullEntries;

    // Amend entries to handle potential overlapping times from preloading
    let hasZeroDurationError = false;
    if (needsRecompute) {
      const entriesByUser = TaskHelpers.getEntriesByUser(newSelected);
      let amendedEntries = [];
      Object.values(entriesByUser).forEach((userEntries) => {
        const { entries: amended, zeroDurationError } = amendEntriesCallback({
          entries: userEntries,
          startTimesChanged: true,
          endTimesChanged: true,
          isBeforeExisting: false,
          prevEntry: null,
        });
        if (zeroDurationError) hasZeroDurationError = true;
        amendedEntries = amendedEntries.concat(amended);
      });
      if (hasZeroDurationError) {
        notification.warn({
          message: 'Zero Duration Entry',
          description: 'Adding these entries would cause an existing entry to have zero duration. Please adjust the times to avoid this conflict.',
        });
        return;
      }
      newSelected = amendedEntries;
    }

    // Need to reformat becaues times may have changed
    const formattedSelected = newSelected.map((task) => (
      TaskHelpers.formatTimeEntryTableEntry({
        entry: task,
        userId: task.userId,
        selectedEntry: task,
        classesMap: classMap,
        phaseMap,
        userMap,
        divisions: divisionMap,
        projectMap,
        costcodeMap,
        localMap,
        tabletMap,
        sageShifts,
        isPreload: true,
        generatedId: uuid(),
        getAllTimesText,
        companySettings: settings,
        projectBillingRateMap,
        positionNameMap,
      })
    ));

    if (responding) {
      updateResponses({
        values: formattedSelected,
        columns,
      });
    } else {
      setPreviewProps({
        ...previewProps,
        selected: formattedSelected,
      });
    }
    closeTeamSelect();
  }, [
    selected,
    previewProps,
    responding,
    preloadedDateMillis,
    defaultProjectId,
    linkedProjectId,
    columns,
    updateResponses,
    teams,
    userMap,
    divisionMap,
    projectMap,
    preloadedStartTime,
    preloadedEndTime,
    settings,
    divisions,
    divisionSet,
    calculateAutoBreak,
    defaultCustomData,
    teamBasedTimeEntries,
    projectBillingRateMap,
    positionNameMap,
  ]);

  const projectIdMap = useMemo(() => getIdMap(projects), [projects]);
  const phaseIdMap = useMemo(() => getIdMap(phases), [phases]);
  const equipmentIdMap = useMemo(() => getIdMap(equipment), [equipment]);

  const customerList = useMemo(() => Object.values(customers), [customers]);
  const vendorList = useMemo(() => Object.values(vendors), [vendors]);
  const parsedFormTemplates = useMemo(() => Object.values(formTemplates), [formTemplates]);

  const customDropdownProps = useMemo(() => ({
    settings,
    filterLabels,
    // *** Data Lists ***
    customers: customerList,
    vendors: vendorList,
    projects,
    users,
    equipment,
    costcodes,
    labels,
    forms,
    phases,
    formTemplates: parsedFormTemplates,
    subContractForms,
    divisions,
    scheduleCategories,

    // *** Subtypes ***
    projectTypes,
    equipmentTypes,

    // *** Maps ***
    bucketTemplateMap,
    boardCardsForDropdown,
    subContractMap,
    subContractCoMap,
    contactAddressBooks,
    globalAddressBooks,
    projectEquipment,
    costcodeMap,
    projectIdMap,
    phaseIdMap,
    userIdMap: userMap,
    vendorIdMap: vendors,
    bucketIdMap,
    equipmentTypeMap,
    divisionMap,
    scheduleOfValues,
    equipmentIdMap,

    // *** Ids ***
    customerId,
    projectId,
    templateId: dropdownTemplateId,
    vendorId,

    // *** Set Ids ***
    setCustomerIds,
    setProjectIds,
    setVendorIds,
    setBucketIds,
    setUserIds: setDropdownUserIds,
    setContactIds,
    setCostcodeIds,
    setSubContractIds,

    // *** x to label ***
    customerToLabel,
    userToLabel,
    vendorToLabel,

    // *** Buckets ***
    selectedBucket,
    selectedBucketTypeToIdMap,
    selectedBucketTypes,
  }), [
    settings,

    customerList,
    vendorList,
    projects,
    users,
    equipment,
    costcodes,
    labels,
    forms,
    phases,
    parsedFormTemplates,
    subContractForms,
    divisions,
    scheduleCategories,

    projectTypes,
    equipmentTypes,

    bucketTemplateMap,
    boardCardsForDropdown,
    subContractMap,
    contactAddressBooks,
    projectEquipment,
    costcodeMap,
    projectIdMap,
    phaseIdMap,
    userMap,
    bucketIdMap,
    equipmentTypeMap,
    divisionMap,
    scheduleOfValues,
    equipmentIdMap,

    customerToLabel,
    userToLabel,
    vendorToLabel,

    setCustomerIds,
    setProjectIds,
    setVendorIds,
    setBucketIds,
    setDropdownUserIds,
    setContactIds,
    setCostcodeIds,
    setSubContractIds,

    customerId,
    projectId,
    vendorId,

    selectedBucket,
    selectedBucketTypeToIdMap,
    selectedBucketTypes,
  ]);

  const selectOptionProps = useMemo(() => ({
    activeCostcodes: costcodes,
    activeProjects,
    activePhases: phases,
    user: selectedUser,
    users,
    teams,
    userDivisions,
    settings,
    localIdMap: localMap,
    classIdMap: classMap,
    costcodeMap,
    filteredActiveCostcodes,
    divisions,
    divisionMap,
  }), [
    costcodes,
    activeProjects,
    phases,
    selectedUser,
    users,
    teams,
    userDivisions,
    settings,
    localMap,
    classMap,
    costcodeMap,
    filteredActiveCostcodes,
    divisions,
    divisionMap,
  ]);

  const {
    columns: tableColumns,
    rows: tableRows,
  } = useMemo(() => (
    timeEntryData({
      onDelete,
      onEdit,
      onUpdate,
      onValueUpdated,
      onAddFromUserClicked,
      onCopy: onCopyClicked,
      groupByUser,
      divisionId: divisions?.[0],
      editableFieldTypes,
      templateDivisionIds: divisions,
      divisionSet,
      columns,
      customFieldMap,
      defaultCustomData,
      isDisplay,
      requiredColumns,
      selectOptionProps,
      customDropdownProps,
      t,
      timezone,
      responding,
      dataSource: selected,
      shouldTransposeData,
      shouldShowEmptyCustomFields,
      enableWarningThreshold,
      warningThreshold,
      preventEdits,
      columnPermissions,
      userPositionId,
      shouldShowAutoBreakColumn,
      usersWithAutoBreak,
      hasPendingTableData,
      paidFlags,
      lockEntryToAuthor,
      groupByUserSortOrder,
      conflictMap,
      onConflictClick,
      responses,
    })
  ), [
    onEdit,
    onDelete,
    onUpdate,
    onValueUpdated,
    onCopyClicked,
    columns,
    customFieldMap,
    isDisplay,
    requiredColumns,
    timezone,
    responding,
    responses,
    selected,
    shouldTransposeData,
    shouldShowEmptyCustomFields,
    enableWarningThreshold,
    warningThreshold,
    preventEdits,
    columnPermissions,
    userPositionId,
    shouldShowAutoBreakColumn,
    usersWithAutoBreak,
    hasPendingTableData,
    paidFlags,
    lockEntryToAuthor,
    groupByUser,
    groupByUserSortOrder,
    customDropdownProps,
    conflictMap,
    onConflictClick,
  ]);

  const formattedSelected = useMemo(() => (
    formatSelectedEntry({
      selectedEntry,
      userId,
      timezone,
    })
  ), [
    selectedEntry,
    userId,
    timezone,
  ]);

  // pre-load entries
  const preloadTasks = useCallback(async (forceReload = false, retainNonPreloadedEntries = false) => {
    const currentValues = responses?.[id]?.values ?? [];
    const hasNonPreloadedEntries = currentValues.some((value) => !value.isPreloaded);
    const linkedStartDate = linkedDateValue?.startTime || null;
    const linkedEndDate = linkedDateValue?.endTime || null;

    if (!forceReload && hasNonPreloadedEntries) return;

    if (!responding) return;

    // Early returns for normal preload (not forced)
    if (!forceReload) {
      if (!preloadExistingEntries) return;
      if (!(preloadProject || linkedProjectId) || !preloadStatuses?.length) return;
      if (preloadDateField && !linkedDateValue) return;
      if (preloadCostcodeDropdown && !linkedCostcodeName && !linkedCostcodeCode) return;

      const { startTime: linkedStart, endTime: linkedEnd } = linkedDateValue ?? {};
      const { startTime: lastStart, endTime: lastEnd } = lastDateValue ?? {};

      const hasSameDate = linkedDateValue
        && linkedStart?.toMillis() === lastStart?.toMillis()
        && linkedEnd?.toMillis() === lastEnd?.toMillis();

      const hasSameProject = linkedProjectId && linkedProjectId === lastProjectId;

      const hasSameCostcode = preloadCostcodeDropdown
        && linkedCostcodeName === lastCostcodeName
        && linkedCostcodeCode === lastCostcodeCode;

      const hasAlreadyPreloaded = preloadProject && hasPreloaded;

      // conditional returns
      if (
        (preloadDateField && hasSameDate && (hasSameProject || hasAlreadyPreloaded) && (!preloadCostcodeDropdown || hasSameCostcode))
        || (!preloadDateField && (hasSameProject || hasAlreadyPreloaded) && (!preloadCostcodeDropdown || hasSameCostcode))
      ) {
        return;
      }
    }

    let taskCustomFieldMap = {};

    const getTaskCustomData = async (taskIds) => {
      try {
        const data = await loadManualEntryData(taskIds, true);
        return data;
      } catch (err) {
        Sentry.captureException(err);
        return {};
      }
    };

    const handleCustomDataField = ({ field, taskCustomFields }) => {
      const taskCustomFieldsRef = taskCustomFields;
      const newResponse = { ...field?.response ?? {} };
      if (field.type === 'yes-no') {
        const newValue = TaskHelpers.parseYesNoResponse(newResponse, false);
        newResponse.value = newValue;
      }
      const newField = {
        ...field,
        ...newResponse,
      };
      delete newField.response;
      taskCustomFieldsRef[field.fieldId] = newField;
    };

    const handleCustomDataFields = ({ customDataArray, taskCustomFields }) => {
      customDataArray.forEach(({ fields = [] }) => {
        fields.forEach((field) => {
          handleCustomDataField({ field, taskCustomFields });
        });
      });
    };

    const processTask = ({ task, customDataMap }) => {
      const { data: customDataArray = [], divisionId: taskDivisionId } = customDataMap?.[task.id] ?? {};
      const divisionId = taskDivisionId ?? task.divisionId;
      let taskCustomFields = {};

      if (taskCustomFieldMap[task.id]) {
        return { task, taskCustomFields: taskCustomFieldMap[task.id], divisionId, customDataArray };
      }

      if (customDataArray.length) {
        handleCustomDataFields({ customDataArray, taskCustomFields });
        taskCustomFieldMap[task.id] = taskCustomFields;
      }

      return {
        task,
        taskCustomFields,
        divisionId,
        customDataArray,
      };
    }

    const handleCustomData = ({ task, customDataMap, payablesSubContractMapOverride }) => {
      let {
        task: updatedTask,
        taskCustomFields,
        divisionId,
        customDataArray,
      } = processTask({ task, customDataMap });

      if (enrichTimeEntryCustomData) {
        const templateCustomData = getDefaultCustomDataForDivision(divisionId);
        const customData = enrichTimeEntryCustomData({
          taskCustomFields,
          divisionId,
          customFields,
          templateCustomData,
          task,
          payablesSubContractMapOverride,
          costcodeIdMap: costcodeMap,
        });
        taskCustomFields = customData;
      }

      return {
        ...task,
        customData: taskCustomFields,
        customDataArray,
        divisionId,
      };
    };

    // Preloads in hidden attribute fields that were hidden for the field worker, but should be visible for the logged in user
    const getTasksWithCustomData = ({ ourTasks, customDataMap, payablesSubContractMapOverride }) => {
      const tasksWithCustomData = ourTasks.map((task) => handleCustomData({
        task,
        customDataMap,
        payablesSubContractMapOverride,
      }));

      return tasksWithCustomData;
    };

    const projectTasks = await getProjectsTasks({
      preloadDateField,
      preloadDateModifier,
      preloadDateCount,
      linkedStart: linkedStartDate,
      linkedEnd: linkedEndDate,
      linkedProjectId: preloadProject ?? linkedProjectId,
      costcodeName: linkedCostcodeName,
      costcodeCode: linkedCostcodeCode,
    });

    const tasksToUse = { tasks: projectTasks };

    // this helper is only used to filter out break tasks or based on status
    const ourTasks = TaskHelpers.getPreloadedEntries({
      preloadDateCount,
      preloadDateModifier,
      preloadStatuses,
      preloadProject,
      preloadDateField,
      linkedProjectId,
      linkedStart: linkedStartDate,
      linkedEnd: linkedEndDate,
      timeEntryUserMap: tasksToUse,
    });

    const taskIds = ourTasks.map((task) => task.id);

    let customDataMap = {};
    let customFileMap = {};

    if (taskIds.length) {
      const {
        dataMap = {},
        fileMap: newFileMap = {},
      } = await getTaskCustomData(taskIds);
      customDataMap = dataMap;
      customFileMap = newFileMap;
    }

    // Retrieves sub contract details for the payable sub contract ids that are used for attribute preloading
    let payablesSubContractMapOverride;
    if (enrichTimeEntryCustomData) {
      const payableSubContractIds = new Set();
      ourTasks.forEach((task) => {
        const {
          taskCustomFields,
          divisionId,
        } = processTask({ task, customDataMap });

        const templateCustomData = getDefaultCustomDataForDivision(divisionId);
        const parsedCustomData = parseCustomDataFromTemplate(taskCustomFields, templateCustomData);
        collectPayableSubContractIds(parsedCustomData).forEach((id) => {
          payableSubContractIds.add(id);
        });
      });

      if (payableSubContractIds.size) {
        try {
          const { data } = await FormsService.getSubContractDetails(Array.from(payableSubContractIds));
          if (data) payablesSubContractMapOverride = data;
        } catch (err) {
          Sentry.captureException(err);
        }
      }
    }

    const tasksWithCustomData = getTasksWithCustomData({
      ourTasks,
      customDataMap,
      payablesSubContractMapOverride,
    });

    let minTime = Number.MAX_SAFE_INTEGER;
    let maxTime = Number.MIN_SAFE_INTEGER;
    const formattedTasks = tasksWithCustomData.map((task) => {
      const newEntry = TaskHelpers.formatTimeEntryTableEntry({
        entry: task,
        userId: task.userId,
        selectedEntry: task,
        classesMap: classMap,
        phaseMap,
        userMap,
        divisions: divisionMap,
        projectMap,
        costcodeMap,
        localMap,
        tabletMap,
        sageShifts,
        isPreload: true,
        generatedId: uuid(),
        getAllTimesText,
        companySettings: settings,
        projectBillingRateMap,
        positionNameMap,
      });

      // Inject localId and local name into the entry from the classId
      const localId = classMap[newEntry.classId]?.localId;
      const localName = localMap[localId]?.name;

      if (localId) {
        newEntry.localId = localId;
        newEntry.local = localName;
      }

      const { startTime, endTime } = newEntry ?? {};
      if (startTime && startTime < minTime) minTime = startTime;
      if (endTime && endTime > maxTime) maxTime = endTime;

      return {
        ...newEntry,
        isPreloaded: true,
      };
    });

    // When force reloading, don't preserve new entries
    const newOrEditedEntries = forceReload && !retainNonPreloadedEntries ? [] : selected.filter((task) => !task.isPreloaded);

    const startDt = DateTime.fromMillis(minTime).startOf('day');
    const endDt = DateTime.fromMillis(maxTime).endOf('day');

    setResponses((prev) => ({
      ...(prev ?? {}),
      [id]: {
        ...(prev?.[id] ?? {}),
        values: newOrEditedEntries.concat(
          sortEntries({
            startDt,
            endDt,
            payrollTasks: formattedTasks,
            userMap,
          }),
        ),
        columns,
        timezone: DateTime.local().zoneName,
        createTimeEntry,
      },
    }));
    setFileMap(customFileMap);
    setLastProjectId(linkedProjectId);
    setLastDateValue(linkedDateValue);
    setLastCostcodeName(linkedCostcodeName);
    setLastCostcodeCode(linkedCostcodeCode);
    setHasPreloaded(true);
  }, [
    responding,
    preloadExistingEntries,
    preloadProject,
    linkedProjectId,
    preloadStatuses,
    preloadDateField,
    linkedDateValue,
    lastDateValue,
    lastProjectId,
    lastCostcodeName,
    lastCostcodeCode,
    hasPreloaded,
    timeEntryUserMap,
    phaseMap,
    userMap,
    divisionMap,
    projectMap,
    costcodeMap,
    localMap,
    classMap,
    sageShifts,
    tabletMap,
    settings,
    selected,
    id,
    setResponses,
    columns,
    createTimeEntry,
    preloadCostcodeDropdown,
    linkedCostcodeName,
    linkedCostcodeCode,
    projectBillingRateMap,
    positionNameMap,
    customFields,
    getDefaultCustomDataForDivision,
    enrichTimeEntryCustomData,
  ]);

  useEffect(() => {
    preloadTasks(false);
  }, [
    configProps,
    id,
    setResponses,
    columns,
    linkedProjectId,
    timeEntryUserMap,
    phaseMap,
    userMap,
    divisionMap,
    projectMap,
    costcodeMap,
    localMap,
    classMap,
    sageShifts,
    tabletMap,
    lastProjectId,
    lastCostcodeName,
    lastCostcodeCode,
    hasPreloaded,
    selected,
    createTimeEntry,
    linkedDateValue,
    settings,
    preloadTasks,
    projectBillingRateMap,
    positionNameMap,
  ]);

  useEffect(() => {
    dispatch(getTablets());
  }, []);

  useEffect(() => {
    resetAutoBreakState();
  }, [id]);

  const handleReloadPreload = useCallback(
    createHandleReloadPreload({
      responses,
      id,
      onReload: () => preloadTasks(true),
    }),
    [preloadTasks, responses, id],
  );

  const relevantCostcodeIds = useMemo(
    () => getRelevantCostcodeIdsFromTimeEntries({ sections, responses }),
    [sections, responses],
  );

  const onLoad = useCallback(() => preloadTasks(true, true), [preloadTasks]);

  useUpdateCostcodeIdMap({
    items: relevantCostcodeIds,
    previousItems: prevRelevantCostcodeIds,
    setPreviousItems: setPrevRelevantCostcodeIds,
    columns: ENTRY_COSTCODE_COLUMNS,
    onLoad,
  });

  const showReloadButton = preloadExistingEntries && responding && !isDisplay && !preventEdits;

  return (
    <Row style={{ marginTop: showCondensedView ? 0 : 15 }}>
      <Row justify='space-between' style={{ width: '100%', marginBottom: 10 }}>
        <Col>
          {title}
        </Col>
        {!isDisplay && !preventEdits && (
          <Space gutter={10}>
            {showReloadButton && (
              <OnTraccrButton
                title="Reload Preloaded Data"
                onClick={handleReloadPreload}
                icon={<ReloadOutlined />}
              />
            )}
            {!lockEntryToAuthor && (
              <FilteredUserSelector
                users={userOpts}
                mode="single"
                onChange={onAddFromUserClicked}
                value={[]}
                placeholder="Add User Row"
                selectStyle={{ width: '250px' }}
              />
            )}
            {((!hideAddFromTeam && !lockEntryToAuthor) || !hideAddNewButton) && (
              <Dropdown
                placement="bottomRight"
                trigger={['click']}
                overlay={(
                  <Menu
                    onClick={({ key }) => {
                      switch (key) {
                        case 'addFromTeam':
                          onAddFromTeamClicked();
                          break;
                        case 'addNew':
                          onAddNewClicked();
                          break;
                      }
                    }}
                  >
                    {!hideAddNewButton && (
                      <Menu.Item key="addNew">
                        Add Row
                      </Menu.Item>
                    )}
                    {!hideAddFromTeam && !lockEntryToAuthor && (
                      <Menu.Item key="addFromTeam">
                        Add from Team
                      </Menu.Item>
                    )}
                  </Menu>
                )}
              >
                <OnTraccrButton
                  title="Add"
                  icon={<DownOutlined />}
                />
              </Dropdown>
            )}
          </Space>
        )}
      </Row>
      { !showCondensedView || selected?.length ? (
        <Table
          className={`form-table-preview ${shouldTransposeData ? 'transposed-table' : ''}`}
          style={{ width: '100%' }}
          columns={tableColumns}
          size="small"
          pagination={false}
          dataSource={tableRows}
          scroll={inAddDrawer ? undefined : { x: 'max-content' }}
          onChange={isDisplay && groupByUser ? handleGroupByUserTableChange : undefined}
          rowClassName={(record) => {
            if (record.pendingTableValue) return 'pending-table-value';
            if (record.isUserHeader) return 'user-header-row';
            return '';
          }}
          rowKey="id"
        />
      ) : (
        <DisplayText title="No Time Entries Selected" style={{ marginBottom: 0 }} />
      )}
      <TimeCardSlider
        visible={showConflictTimeCard}
        onClose={() => setShowConflictTimeCard(false)}
        user={conflictTimeCard.user}
        initialEntries={conflictTimeCard.entries}
        initialHistory={conflictTimeCard.history}
        defaultDate={conflictTimeCard.date}
        isDisplay
        onEditClicked={noop}
        setShowFormDrawer={noop}
        updateManualEntryData={noop}
        isCopying={false}
        setIsCopying={noop}
      />
      {!isDisplay && (
        <>
          {!hideAddFromTeam && !lockEntryToAuthor
          && (
          <TimeEntryTableTeamSelection
            visible={showTeamSelect}
            onClose={closeTeamSelect}
            onAdd={onAddFromTeam}
            divisions={divisions}
          />
          )}
          <ManualEntryFormDrawer
            visible={showDrawer}
            onClose={closeDrawer}
            onSubmit={onSubmitEntry}
            onManualProjectIdChange={handleManualProjectIdChange}
            selectedEntry={formattedSelected}
            defaultClassId={defaultClassId}
            defaultProjectId={defaultProjectId}
            defaultDate={preloadedSmartDateSQL || preloadedDateSQL}
            defaultStartTime={preloadedSmartStartTime ?? preloadedStartTime}
            defaultEndTime={preloadedSmartStartTime == null ? preloadedEndTime : null}
            userId={userId}
            divisionIds={divisions}
            lockedDivisionId={divisions && !isMultiDivision ? divisions?.[0] : null}
            user={selectedUser}
            hideUserSelector={!userIds.length}
            userIds={userIds}
            fileMap={fileMap}
            allowInlineTimes={paidFlags?.includes(TIME_TYPE_FLAG)}
            isTimeEntryTable
          />
          <Modal
            visible={showCopyModal}
            onOk={handleCopyToUsers}
            onCancel={closeCopyModal}
            okText="Copy"
            cancelText="Cancel"
            okButtonProps={{ disabled: selectedUsersForCopy.length === 0 }}
            width={600}
            bodyStyle={{ padding: '5px 20px' }}
          >
            <div style={{ marginBottom: 16, background: 'transparent', borderRadius: 20 }}>
              <p>Select users to copy this entry to:</p>
            </div>
            <FilteredUserSelector
              users={userOpts}
              mode="multiple"
              onChange={setSelectedUsersForCopy}
              value={selectedUsersForCopy}
              placeholder="Select users"
              filterStyle={{
                display: 'flex',
                justifyContent: 'start',
              }}
            />
          </Modal>
        </>
      )}
    </Row>
  );
}

/* eslint-disable react/forbid-prop-types */
TimeEntryTablePreview.propTypes = {
  title: PropTypes.node,
  columns: PropTypes.array,
  previewProps: PropTypes.object,
  id: PropTypes.string,
  setPreviewProps: PropTypes.func,
  isDisplay: PropTypes.bool,
  setResponses: PropTypes.func,
  responses: PropTypes.object,
  responding: PropTypes.bool,
  projectId: PropTypes.string,
  divisions: PropTypes.arrayOf(PropTypes.string),
  configProps: PropTypes.shape({
    hideAddNewButton: PropTypes.bool,
    requiredColumns: PropTypes.bool,
  }),
  showCondensedView: PropTypes.bool,
  inAddDrawer: PropTypes.bool,
  t: PropTypes.func.isRequired,
  templateId: PropTypes.string.isRequired,
  createTimeTableIds: PropTypes.arrayOf(PropTypes.string),
  bucketTemplateMap: PropTypes.shape({}),
  boardCardsForDropdown: PropTypes.shape({}),
  subContractMap: PropTypes.shape({}),
  contactAddressBooks: PropTypes.shape({}),
  projectEquipment: PropTypes.shape({}),
  projectIdMap: PropTypes.shape({}),
  phaseIdMap: PropTypes.shape({}),
  subContractCoMap: PropTypes.shape({}),
  scheduleOfValues: PropTypes.shape({}),
  customerToLabel: PropTypes.shape({}),
  userToLabel: PropTypes.shape({}),
  vendorToLabel: PropTypes.shape({}),
  setCustomerIds: PropTypes.func,
  setProjectIds: PropTypes.func,
  setVendorIds: PropTypes.func,
  setContactIds: PropTypes.func,
  setCostcodeIds: PropTypes.func,
  setUserIds: PropTypes.func,
  setBucketIds: PropTypes.func,
  setSubContractIds: PropTypes.func,
  customerId: PropTypes.string,
  vendorId: PropTypes.string,
  selectedBucket: PropTypes.shape({}),
  selectedBucketTypeToIdMap: PropTypes.shape({}),
  selectedBucketTypes: PropTypes.arrayOf(PropTypes.shape({})),
  costcodes: PropTypes.arrayOf(PropTypes.shape({})),
  costcodeMap: PropTypes.object,
  fetchProjectCostcodes: PropTypes.func,
  enrichTimeEntryCustomData: PropTypes.func,
  poCloseMode: PropTypes.bool,
};

TimeEntryTablePreview.defaultProps = {
  title: null,
  columns: [],
  previewProps: {},
  id: null,
  setPreviewProps: null,
  isDisplay: false,
  setResponses: null,
  responses: {},
  responding: false,
  projectId: null,
  divisions: [],
  configProps: {},
  showCondensedView: false,
  inAddDrawer: false,
  createTimeTableIds: [],
  bucketTemplateMap: {},
  boardCardsForDropdown: {},
  subContractMap: {},
  contactAddressBooks: {},
  projectEquipment: {},
  projectIdMap: {},
  phaseIdMap: {},
  subContractCoMap: {},
  scheduleOfValues: {},
  customerToLabel: {},
  userToLabel: {},
  vendorToLabel: {},
  setCustomerIds: null,
  setProjectIds: null,
  setVendorIds: null,
  setContactIds: null,
  setCostcodeIds: null,
  setUserIds: null,
  setBucketIds: null,
  setSubContractIds: null,
  customerId: null,
  vendorId: null,
  selectedBucket: null,
  selectedBucketTypeToIdMap: {},
  selectedBucketTypes: [],
  costcodes: [],
  costcodeMap: {},
  fetchProjectCostcodes: null,
  enrichTimeEntryCustomData: null,
  poCloseMode: false,
};
